import {
  Award,
  Calendar,
  CheckCircle2,
  Clock,
  DollarSign,
  FileText,
  GraduationCap,
  MapPin,
  MessageCircle,
  Smartphone,
  Sun,
  Target,
  Users,
  BookOpen,
  type LucideIcon,
} from 'lucide-react';
import type { Year3FoundationsPageData } from '@/pages/programs/templates/Year3FoundationsTemplate';
import {
  FOUNDATIONS_YEAR_CONTENT,
  type FoundationsYearContent,
} from '@/data/programs/foundationsYearContent';
import { foundationsSessionDuration } from '@/config/courseDurations';
import { isCourseFamilyAvailableIn } from '@/config/courseStateMapping';

/**
 * Config for the config-driven Foundations year-level pages (WA + VIC gaps).
 * Existing NSW and VIC hand-written pages are untouched and not listed here.
 */
export type FoundationsState = 'WA' | 'VIC';
export type FoundationsSubject = 'english' | 'maths';

type StateConfig = {
  code: FoundationsState;
  curriculum: string;
  year0Label: string;
  year0Slug: string;
  years: number[];
};

export const FOUNDATIONS_STATE_CONFIG: Record<FoundationsState, StateConfig> = {
  WA: {
    code: 'WA',
    curriculum: 'Western Australian Curriculum',
    year0Label: 'Pre-primary',
    year0Slug: 'pre-primary',
    years: [0, 1, 2, 3, 4, 5, 6, 7, 8],
  },
  VIC: {
    code: 'VIC',
    curriculum: 'Victorian Curriculum',
    year0Label: 'Prep',
    year0Slug: 'prep',
    years: [3, 4, 5, 6],
  },
};

export type FoundationsPageKey = {
  state: FoundationsState;
  year: number;
  subject: FoundationsSubject;
};

const SUBJECT_LABEL: Record<FoundationsSubject, string> = { english: 'English', maths: 'Maths' };

export const yearLabelFor = (state: FoundationsState, year: number) =>
  year === 0 ? FOUNDATIONS_STATE_CONFIG[state].year0Label : `Year ${year}`;

export const yearSlugFor = (state: FoundationsState, year: number) =>
  year === 0 ? FOUNDATIONS_STATE_CONFIG[state].year0Slug : `year-${year}`;

export const foundationsRoute = ({ state, year, subject }: FoundationsPageKey) =>
  `/programs/${state.toLowerCase()}/${yearSlugFor(state, year)}-foundations-${subject}-tutoring`;

export const FOUNDATIONS_YEAR_PAGES: FoundationsPageKey[] = (
  Object.keys(FOUNDATIONS_STATE_CONFIG) as FoundationsState[]
).flatMap((state) =>
  FOUNDATIONS_STATE_CONFIG[state].years.flatMap((year) =>
    (['english', 'maths'] as FoundationsSubject[]).map((subject) => ({ state, year, subject })),
  ),
);

export const foundationsSeoTitle = ({ state, year, subject }: FoundationsPageKey) =>
  `${yearLabelFor(state, year)} ${SUBJECT_LABEL[subject]} Tutoring ${state} - Small Groups | Schoolola`;

const ICONS: Record<string, LucideIcon> = {
  Award,
  Calendar,
  CheckCircle2,
  Clock,
  DollarSign,
  FileText,
  GraduationCap,
  MapPin,
  MessageCircle,
  Smartphone,
  Sun,
  Target,
  Users,
  BookOpen,
};

export type ResolvedPricing = {
  pricePerSession: number | null;
  booksCost: number | null;
};

const money = (n: number) => `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;

/** Extra state-specific related links that obey courseStateMapping. */
const stateExtras = (state: FoundationsState, year: number) => {
  const extras: { tag: string; name: string; desc: string; href: string }[] = [];
  if (state === 'WA' && year === 6 && isCourseFamilyAvailableIn('gate', 'WA')) {
    extras.push({
      tag: 'Year 6 · WA',
      name: 'ASET / GATE Test Preparation',
      desc: 'Preparation for the WA ASET / GATE test',
      href: '/courses/aset-gate-test-preparation',
    });
  }
  if (state === 'VIC' && (year === 5 || year === 6) && isCourseFamilyAvailableIn('scholarship_vic', 'VIC')) {
    extras.push({
      tag: 'Year 5-6 · VIC',
      name: 'Scholarship Exam Course (SEC)',
      desc: 'Victoria scholarship exam preparation',
      href: '/programs/scholarship-exam-course',
    });
  }
  return extras;
};

export const getFoundationsContent = (key: FoundationsPageKey): FoundationsYearContent =>
  FOUNDATIONS_YEAR_CONTENT[`${key.year}-${key.subject}`];

export const buildFoundationsPage = (
  key: FoundationsPageKey,
  pricing: ResolvedPricing,
): Year3FoundationsPageData => {
  const { state, year, subject } = key;
  const cfg = FOUNDATIONS_STATE_CONFIG[state];
  const c = getFoundationsContent(key);
  const duration = foundationsSessionDuration(year);
  const yearLabel = yearLabelFor(state, year);
  const subj = SUBJECT_LABEL[subject];
  const other: FoundationsSubject = subject === 'english' ? 'maths' : 'english';

  const priceText = pricing.pricePerSession != null ? money(pricing.pricePerSession) : 'The session fee';
  const booksText =
    pricing.booksCost == null
      ? ''
      : pricing.booksCost > 0
        ? `The book pack is ${money(pricing.booksCost)}, charged separately.`
        : 'There is no separate book pack charge.';

  const r = (s: string) =>
    s
      .split('{STATE}').join(state)
      .split('{CURRICULUM}').join(cfg.curriculum)
      .split('{YEAR0}').join(cfg.year0Label)
      .split('{MINUTES}').join(String(duration.minutes))
      .split('{PRICE}').join(priceText)
      .split('{BOOKS}').join(booksText)
      .replace(/\s+$/, '');

  const related = [
    {
      tag: `${yearLabel} · ${state}`,
      name: `${yearLabel} ${SUBJECT_LABEL[other]}`,
      desc: c.siblingDesc,
      href: foundationsRoute({ state, year, subject: other }),
    },
    ...(c.naplanRelated
      ? [
          {
            tag: `${yearLabel} · ${state}`,
            name: `NAPLAN ${yearLabel}`,
            desc: 'Targeted NAPLAN preparation',
            href: '/programs/naplan-preparation',
          },
        ]
      : []),
    ...stateExtras(state, year),
  ];

  const route = foundationsRoute(key);

  return {
    route,
    breadcrumb: [`${state} programs`, 'Foundations', `${yearLabel} ${subj}`],
    pill: `${yearLabel} · ${cfg.curriculum}`,
    h1: `${yearLabel} ${subj} tutoring in ${state}`,
    subtitle: r(c.subtitle),
    quickFacts: c.quickFacts.map((f) => ({
      icon: ICONS[f.icon],
      text: f.icon === 'Clock' ? duration.short : f.icon === 'DollarSign' ? '' : r(f.text),
    })),
    courseDetailsHeading: `${yearLabel} ${subj}`,
    pricingHeading: `${yearLabel} ${subj}`,
    relatedHeading: `More for ${yearLabel} students`,
    yearLevel: year,
    stateCode: state,
    tabs: c.tabs.map((t) => ({
      ...t,
      icon: ICONS[t.icon],
      name: r(t.name),
      sub: r(t.sub),
      items: t.items.map((i) => ({
        label: r(i.label),
        name: r(i.name),
        heading: r(i.heading),
        para: r(i.para),
        bullets: i.bullets.map(r),
      })),
    })),
    inclusions: c.inclusions.map((i) => ({ ...i, icon: ICONS[i.icon], name: r(i.name), desc: r(i.desc) })),
    faq: c.faq.map((f) => ({ q: r(f.q), a: r(f.a) })),
    related,
    seo: {
      title: foundationsSeoTitle(key),
      description: r(c.seoDescription),
      schemaCourseName: `${yearLabel} ${subj} Tutoring - ${state}`,
      schemaDescription: r(c.schemaDescription),
    },
    requireDbPrice: true,
    courseWorkload: duration.iso,
    socialMeta: true,
    breadcrumbSchema: [
      { name: 'Home', url: 'https://schoolola.com.au/' },
      { name: `${state} programs`, url: `https://schoolola.com.au/programs/${state.toLowerCase()}` },
      { name: `${yearLabel} ${subj}`, url: `https://schoolola.com.au${route}` },
    ],
  };
};
